import { useCallback, useEffect } from 'react';
import { GoogleTokenConfig } from './cfg';
import { RootState } from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';
import { addSpreadsheetData } from '../../lib/redux/sheets/slice';

enum TrellusColumn {
  DO_NOT_CALL = 'Do not call again (Trellus)',
  LAST_CALL_ATTEMPT = 'TRELLUS_Last_Call_Attempt',
  LAST_CALL_OUTCOME = 'TRELLUS_Last_Call_Outcome',
  LAST_TIME_HUMAN = 'TRELLUS_Last_Time_Human',
  TOTAL_CALL_ATTEMPTS = 'TRELLUS_Total_Call_Attempts',
  CALL_NOTES = 'TRELLUS_Call_Notes',
  ALL_PREVIOUS_CALLS = 'TRELLUS_All_Previous_Calls',
  ALL_DISPOSITIONS = 'TRELLUS_All_Dispositions'
}

const TRELLUS_COLUMNS = Object.values(TrellusColumn);

// Track which sheet GIDs already have Trellus columns to avoid re-adding
const TRELLUS_COLUMNS_ADDED_KEY = 'trellus_columns_added';

function getTrellusColumnsAdded() {
  const saved = localStorage.getItem(TRELLUS_COLUMNS_ADDED_KEY);
  if (!saved) return {};
  try { return JSON.parse(saved); }
  catch (error) { return {}; }
}

function setTrellusColumnsAdded(sheetGid: string) {
  const current = getTrellusColumnsAdded();
  current[sheetGid] = true;
  try { localStorage.setItem(TRELLUS_COLUMNS_ADDED_KEY, JSON.stringify(current)); }
  catch (error) { }
}

function hasColumnsAdded(sheetGid: string): boolean {
  const added = getTrellusColumnsAdded();
  return added[sheetGid] === true;
}

// Helper function to find the correct sheet by gid or fall back to first sheet
function findSheetByGid(sheets: any[], gid?: string) {
  if (!sheets || sheets.length === 0) return null;
  
  if (gid) {
    const targetSheet = sheets.find(sheet => sheet.properties?.sheetId?.toString() === gid);
    if (targetSheet) return targetSheet;
  }
  
  // Fall back to first sheet
  return sheets[0];
}

export function useEnsureTrellusColumns() {
  const dispatch = useDispatch();
  const { makeApiCall } = useSpreadsheetApiCall();
  
  // Remove automatic triggering - now only called manually when needed

  const ensureTrellusColumns = useCallback(async (spreadsheetId: string, sheetGid?: string, onColumnStructureChanged?: (spreadsheetId: string, sheetName: string) => void): Promise<boolean> => {
    const startTime = Date.now();
    const operationId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    try {

      if (!spreadsheetId) {
        return false;
      }
      
      // Check if we already added columns to this sheet (but still validate actual headers)
      const columnsMarkedAsAdded = sheetGid && hasColumnsAdded(sheetGid);
      if (columnsMarkedAsAdded) {
      }
      
      const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title,sheets.properties`;
      const metadataResult = await makeApiCall({ url: metadataUrl });
      
      if (!metadataResult.success || !metadataResult.data) {
        return false;
      }
      
      const metadata = metadataResult.data;
      const targetSheet = findSheetByGid(metadata.sheets || [], sheetGid);
      if (!targetSheet) {
        return false;
      }
      
      const sheetId = targetSheet.properties.sheetId;
      const sheetTitle = targetSheet.properties.title;
      const actualSheetGid = sheetId.toString();

      // Get current header row
      const headerUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}!1:1`;
      const headerResult = await makeApiCall({ url: headerUrl });
      
      if (!headerResult.success || !headerResult.data) {
        return false;
      }

      const currentHeaders = headerResult.data.values?.[0] || [];
      if (currentHeaders.length === 0) {
        return false;
      }
      
      
      const missingColumns = TRELLUS_COLUMNS.filter(column => !currentHeaders.includes(column));


      if (missingColumns.length === 0) {
        // Make sure localStorage is in sync
        if (actualSheetGid) {
          setTrellusColumnsAdded(actualSheetGid);
        }
        return true;
      }

      // If localStorage said columns were added but they're missing, clear the cache and log the issue
      if (columnsMarkedAsAdded) {

        // Clear the incorrect cache entry
        const current = getTrellusColumnsAdded();
        delete current[sheetGid!];
        try {
          localStorage.setItem(TRELLUS_COLUMNS_ADDED_KEY, JSON.stringify(current));
        } catch (error) {
        }
      }

      // Track if we need to refresh cached data (when columns are added/inserted)
      let needsDataRefresh = false;

      // Handle TRELLUS_Remove insertion at the front (requires inserting a new column)
      const trellusRemoveIndex = missingColumns.indexOf(TrellusColumn.DO_NOT_CALL);
      let batchRequests = [];
      
      if (trellusRemoveIndex !== -1) {
        // Insert a new column at the beginning (position 0)
        batchRequests.push({
          insertDimension: {
            range: {
              sheetId: sheetId,
              dimension: 'COLUMNS',
              startIndex: 0,
              endIndex: 1
            }
          }
        });
        
        // Remove TRELLUS_Remove from missingColumns since we're handling it separately
        missingColumns.splice(trellusRemoveIndex, 1);
        needsDataRefresh = true; // Column insertion shifts all data
      }
      
      // If we need to insert TRELLUS_Remove column, do the batch update first with retry
      if (batchRequests.length > 0) {
        const batchResult = await makeApiCall({
          url: `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
          method: 'POST',
          body: { requests: batchRequests },
          maxRetries: 3
        });

        if (!batchResult.success) {
          return false;
        }

      }
      
      // Now update the header row with proper headers - BE VERY CAREFUL HERE
      let newHeaders = [...currentHeaders];

      if (trellusRemoveIndex !== -1) {
        // TRELLUS_Remove goes in the first column, existing headers shift right
        newHeaders = [TrellusColumn.DO_NOT_CALL, ...currentHeaders];
      }

      // CRITICAL: Only add missing columns at the very end to avoid overwriting user data
      if (missingColumns.length > 0) {
        // Simply append missing columns to the end - NEVER insert in the middle
        const finalHeaders = [...newHeaders];
        missingColumns.forEach((column) => {
          finalHeaders.push(column);
        });

        newHeaders = finalHeaders;
        needsDataRefresh = true; // New columns added

      }
      
      // Update the header row directly (not using queue - must guarantee execution) with retry
      const updateResult = await makeApiCall({
        url: `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}!1:1?valueInputOption=RAW`,
        method: 'PUT',
        body: { values: [newHeaders] },
        maxRetries: 3
      });

      if (!updateResult.success) {
        return false;
      }
      

      // Notify that column structure changed so caches can be invalidated
      if (needsDataRefresh && onColumnStructureChanged) {
        onColumnStructureChanged(spreadsheetId, sheetTitle);
      }

      // If we made structural changes (added/inserted columns), refresh the cached data
      if (needsDataRefresh) {
        // Fetch updated spreadsheet data
        const dataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}`;
        const dataResult = await makeApiCall({ url: dataUrl });

        if (dataResult.success && dataResult.data) {
          // Update the cached data in Redux
          const updatedSpreadsheetData = {
            id: metadata.spreadsheetId,
            name: metadata.properties.title,
            sheets: metadata.sheets.map((sheet: any) => ({
              title: sheet.properties.title,
              sheetId: sheet.properties.sheetId,
              rowCount: sheet.properties.gridProperties?.rowCount || 0,
              columnCount: sheet.properties.gridProperties?.columnCount || 0,
            })),
            data: dataResult.data.values || [],
          };

          dispatch(addSpreadsheetData(updatedSpreadsheetData));
        } else {
        }
      }

      // Mark this sheet as having columns added
      if (actualSheetGid) {
        setTrellusColumnsAdded(actualSheetGid);
      }

      const totalTime = Date.now() - startTime;
      return true;
    } catch (error) {
      const totalTime = Date.now() - startTime;
      return false;
    }
  }, [makeApiCall, dispatch])

  return { ensureTrellusColumns };
} 